import React, { useEffect, useMemo } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import ProductPhoto from './ProductPhoto';
import { brandProducts } from './brand-products';

const FILTERS = ['ALL', 'APPAREL', 'DRINKWARE', 'HOME'];
const categoryLabel = (product) => {
  if (product.category === 'tee' || product.category === 'polo') return 'APPAREL';
  if (product.category === 'mug') return 'HOME';
  if (product.category === 'tumbler') return 'DRINKWARE';
  return 'OTHER';
};

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const key = (params.get('category') || 'ALL').toUpperCase();
  const filter = FILTERS.includes(key) ? key : 'ALL';
  const setFilter = key => {
    const copy = new URLSearchParams(params);
    if (key === 'ALL') copy.delete('category'); else copy.set('category', key.toLowerCase());
    setParams(copy, {replace:true});
    requestAnimationFrame(() => document.getElementById('catalog-grid')?.scrollIntoView({block:'start'}));
  };
  const suffix = filter === 'ALL' ? '' : '?category=' + filter.toLowerCase();
  const location = useLocation();
  const items = useMemo(() => brandProducts.map((product, index) => ({
    product, id: String(index + 1).padStart(2, '0'), category: categoryLabel(product)
  })).filter(item => filter === 'ALL' || item.category === filter), [filter]);
  useEffect(() => {
    document.title = (filter === 'ALL' ? 'ALL ITEMS' : filter + ' ITEMS') + ' | PARALYZE AREA';
    return () => { document.title = 'PARALYZE AREA'; };
  }, [filter]);
  useEffect(() => {
    const slug = decodeURIComponent(location.hash.slice(1));
    if (slug) requestAnimationFrame(() => document.getElementById(slug)?.scrollIntoView({block:'start'}));
  }, [location.hash, filter]);
  return (
    <main className="catalog-page">
      <header className="catalog-nav">
        <Link to="/" className="catalog-brand">PARALYZE AREA</Link>
        <div className="catalog-nav-right"><Link to="/">HOME</Link><Link to="/museum">EXHIBITION ↗</Link></div>
      </header>
      <section className="catalog-hero">
        <div className="catalog-kicker">DROP 001 / 07 DESIGN STUDIES</div>
        <h1>THE<br/>ITEMS<span className="catalog-dot">.</span></h1>
        <p>サソリ、クラゲ、ヘビ、フグ。7つのデザインを選んで詳細へ。掲載画像はAI MOCKUP、すべて販売準備中です。</p><a className="catalog-gallery-jump" href="#catalog-grid">7つのデザインを見る ↓</a>
      </section>
      <nav className="catalog-filters" aria-label="アイテムのカテゴリ" >
        {FILTERS.map(item => <button key={item} type="button"
          aria-pressed={filter === item} className={'catalog-filter ' + (filter === item ? 'active' : '')}
          onClick={() => setFilter(item)}>{item} <span>{item === 'ALL' ? brandProducts.length : brandProducts.filter(p => categoryLabel(p) === item).length}</span></button>)}
        <span className="catalog-filter-count" role="status">{String(items.length).padStart(2,'0')} / 07</span>
      </nav>
      <section id="catalog-grid" className="catalog-grid" data-filter={filter} aria-label="アイテム一覧">
        {items.map(({product, id, category}) => (
          <article className="catalog-card" key={product.slug} id={product.slug} data-product={product.slug}>
            <Link className="catalog-media" to={'/items/' + product.slug + suffix} aria-label={product.name + ' の商品詳細'}>
              <ProductPhoto product={product} loading={items[0]?.product.slug === product.slug ? 'eager' : 'lazy'}/>
              <span className="catalog-index">{id} / 07</span>
            </Link>
            <div className="catalog-card-meta">
              <div><span className="catalog-category">{category} · AI MOCKUP</span><h2>{product.name}</h2></div>
              <div className="catalog-status"><strong>販売準備中</strong><span>仕様・価格未確定</span></div>
            </div>
            <Link className="catalog-detail-link" to={'/items/' + product.slug + suffix}>デザインの詳細を見る <span aria-hidden="true">↗</span></Link>
          </article>
        ))}
      </section>
      {items.length === 0 && <p className="catalog-empty">該当するアイテムはありません。<button onClick={() => setFilter('ALL')}>すべて表示</button></p>}
      <footer className="catalog-footer">
        <Link to="/">← ブランドトップ</Link>
        <Link to="/museum">展示を体験する（商品詳細とは別） ↗</Link>
        <span>AI MOCKUP / 販売準備中</span>
        <a href="https://eeek-inc.com/" target="_blank" rel="noopener noreferrer">運営会社 ↗</a>
      </footer>
    </main>
  );
}
